import { Dispatch, useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { LoadUrl, LoadUrlProps } from '@cash-web/shared-flow-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { getRouteInfo } from '@cash-web/shared-util-route-info'

import { FlowReducerAction, FlowState } from './flowReducer'
import { useFlowLogging } from './useFlowLogging'

/**
 * Used to handle routes / urls that are used in blockers. This will handle the navigation to the correct route
 * it will also automatically load the data and update the flow tracking name and log the start blocker flow event
 */
export const useLoadUrl = ({ state, dispatch }: { state: FlowState; dispatch: Dispatch<FlowReducerAction> }) => {
  const { logLoadUrl } = useFlowLogging()
  const navigate = useNavigate()

  const journey = useJourneyContext()

  const loadUrl: LoadUrl = useCallback(
    async ({ trackingName, url, options, target = '_blank' }: LoadUrlProps) => {
      const routeInfo = getRouteInfo(url)
      if (routeInfo == null) {
        return
      }

      logLoadUrl({ state, trackingName, routeInfo })
      dispatch({ type: 'LOAD_URL', url, trackingName, routeInfo })
      switch (routeInfo?.type) {
        case 'external': {
          // we are redirecting externally, but this flow isn't done yet.
          // we are no longer loading
          dispatch({ type: 'SET_LOADING', isLoading: false })

          routeInfo.redirect(target)
          break
        }
        case 'internal': {
          // we are redirecting internally so let's complete the flow, before we navigate
          dispatch({ type: 'FLOW_COMPLETE' })
          if (routeInfo.url != null) {
            // Close out of all journeys if navigating to an internal route.
            journey.idle()
            navigate(routeInfo.url, options)
          } else {
            routeInfo.redirect()
          }
          break
        }
        case 'journey': {
          // start journey
          journey.navigate(routeInfo.journey, {
            ...options,
            replace: routeInfo.replace != null ? routeInfo.replace : options?.replace,
          })
          break
        }
        default: {
          break
        }
      }
    },
    [dispatch, journey, logLoadUrl, navigate, state]
  )
  return loadUrl
}
